/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpParams } from '@angular/common/http';
import { CustomEncoder } from '@iot-platform/core';
import { CommonApiListResponse, CommonApiRequest, CommonApiRequestBody, CommonApiResponse, Filter, Pagination } from '@iot-platform/models/common';
import { I4BGridFilterOptions } from '@iot-platform/models/grid-engine';

export class ApiHelpers {
  static getHttpParams(request?: CommonApiRequest): HttpParams {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    if (request) {
      if (request.limit !== null && request.limit !== undefined && request.limit !== -1) {
        params = params.set('limit', request.limit.toString(10));
        params = params.set('page', request.page ? request.page.toString(10) : '0');
      }

      if (request.filters && request.filters.length > 0) {
        request.filters.forEach((filter: Filter) => {
          params = params.append(filter.criteriaKey, filter.value);
        });
      }
      if (request.variables && request.variables.length > 0) {
        request.variables.forEach((variable) => {
          params = params.append('expandVariableName', variable.toLowerCase());
        });
      }
      if (request.tags && request.tags.length > 0) {
        request.tags.forEach((tagCategory) => {
          params = params.append('expandTagCategory', tagCategory);
        });
      }
    }
    return params;
  }

  static getHttpResponse<T>(
    data: CommonApiListResponse<T> | T[],
    options?: {
      page: number;
      limit: number;
      total?: number;
    }
  ): CommonApiResponse<T, Pagination> {
    let pagination: Pagination;
    if (Array.isArray(data)) {
      const limit = options ? options.limit : 10;
      let total = data?.length;
      if (options && options.total && total > 0) {
        total = options.total;
      }
      pagination = {
        currentPage: options ? options.page : 0,
        limit,
        hasMore: true,
        maxPage: Number.parseInt(`${total / limit}`),
        total
      };
    } else {
      pagination = {
        currentPage: typeof data?.page?.curPage === 'number' ? data?.page?.curPage : Number.parseInt(data?.page?.curPage),
        hasMore: data?.page?.hasMore,
        limit: data?.page?.limit,
        maxPage: data?.page?.maxPage,
        total: data?.page?.total
      };
    }
    return {
      data: Array.isArray(data) ? data : data.content,
      pagination
    };
  }

  static getHttpRequestBody(request: CommonApiRequest): CommonApiRequestBody {
    const body: CommonApiRequestBody = {
      pagination: {
        limit: request.limit,
        page: request.page
      },
      filters: {}
    };

    // Group filters by criteriaKey, handling range operators
    const filterMap = new Map<string, { operator: I4BGridFilterOptions; values: any[] }>();

    request.filters?.forEach((filter) => {
      let key = filter.criteriaKey;
      const operator = filter.criteriaOperator || I4BGridFilterOptions.EQUALS;
      // Handle range operators as separate keys
      if (operator === I4BGridFilterOptions.IN_RANGE_START) {
        key = `${filter.criteriaKey}Start`;
      } else if (operator === I4BGridFilterOptions.IN_RANGE_END) {
        key = `${filter.criteriaKey}End`;
      }
      if (!filterMap.has(key)) {
        filterMap.set(key, { operator, values: [] });
      }
      if (filter.value !== null && filter.value !== undefined) {
        filterMap.get(key).values.push(filter.value);
      }
    });

    // Variables (expandVariableName)
    if (request.variables && request.variables.length > 0) {
      const key = 'expandVariableName';
      if (!filterMap.has(key)) {
        filterMap.set(key, { operator: I4BGridFilterOptions.EQUALS, values: [] });
      }
      filterMap.get(key).values = request.variables.filter((variable) => variable !== undefined && variable !== null).map((variable) => variable.toLowerCase());
    }

    // Tags (expandTagCategory)
    if (request.tags && request.tags.length > 0) {
      const key = 'expandTagCategory';
      if (!filterMap.has(key)) {
        filterMap.set(key, { operator: I4BGridFilterOptions.EQUALS, values: [] });
      }
      filterMap.get(key).values = request.tags.filter((tag) => tag !== undefined && tag !== null);
    }

    // Convert map to object
    filterMap.forEach((value, key) => {
      body.filters[key] = value;
    });

    return body;
  }
}
